import {dehydrate, QueryClient} from '@tanstack/react-query';
import {GetStaticPaths, GetStaticProps, GetStaticPropsContext} from 'next';
import React from 'react';

import {getAllGwgAccelerators, getGwgAccelerator} from '@udemy/career-academies';
import {RENDER_MODES} from '@udemy/nextjs-core';

import {getLayout} from 'src/components/layout';
import {
    GoogleAiPathPageFeature,
    GoogleAiPathPageFeatureProps,
} from 'src/features/google-ai-path-page';
import {useGetAcceleratorEnrollmentQuery} from 'src/generated/graphql';
import {getCompactTranslations} from 'src/lib-server/locale.server';
import {DEFAULT_REVALIDATE_SECONDS} from 'src/lib/constants';
import {getEnrichedTopics} from 'src/lib/get-enriched-topics';
import {getGoogleInstructorId} from 'src/lib/get-google-instructor-id';
import {getInstructorData} from 'src/lib/get-instructor-data';
import {fetcher} from 'src/lib/gql-fetcher';
import {getStaticAppContext} from 'src/pages/api/context.api';
import {AppContextData} from 'src/types/page-props';

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export const getStaticProps: GetStaticProps = async (context: GetStaticPropsContext) => {
    const locale = context.locale as string;
    const path = context.params?.path as string;
    const translations = await getCompactTranslations(locale);
    const client = new QueryClient();

    // Resolve appContext
    const [staticData] = await Promise.all([getStaticAppContext({locale})]);
    const appContext: AppContextData = {
        ...staticData,
        // application specific context can go here
    };

    const acceleratorData = await (
        getGwgAccelerator as (
            path: string,
            locale: string,
            options?: {customGqlFetcher?: typeof fetcher},
        ) => ReturnType<typeof getGwgAccelerator>
    )(path, locale, {
        customGqlFetcher: fetcher,
    });

    if (!acceleratorData) {
        return {
            notFound: true,
        };
    }

    const instructorData = await getInstructorData({userId: getGoogleInstructorId()}, locale);

    const topics = await getEnrichedTopics(acceleratorData.secondaryTopics, locale);

    const allGwgAcceleratorsData = await (
        getAllGwgAccelerators as (
            locale: string,
            options?: {customGqlFetcher?: typeof fetcher},
        ) => ReturnType<typeof getAllGwgAccelerators>
    )(locale, {
        customGqlFetcher: fetcher,
    });

    const staticProps = {
        props: {
            appContext,
            dehydratedState: dehydrate(client),
            locale,
            path,
            pageKey: 'google_ai_path',
            renderMode: RENDER_MODES.ISR, // supports render functions in _document.tsx and _app.tsx.
            translations,
            accelerator: acceleratorData,
            instructor: instructorData,
            allGwgAccelerators: allGwgAcceleratorsData || [],
        },
        revalidate: DEFAULT_REVALIDATE_SECONDS,
    };

    if (topics.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (staticProps.props as any).topics = topics;
    }

    return staticProps;
};

export default function GoogleAiPathPage(props: GoogleAiPathPageFeatureProps) {
    const enrollmentQuery = useGetAcceleratorEnrollmentQuery(
        {id: props.accelerator.numericId.toString()},
        {enabled: Boolean(props.accelerator?.numericId)},
    );
    const isEnrolledInAccelerator = Boolean(enrollmentQuery.data?.careerAccelerator?.isEnrolled);

    return <GoogleAiPathPageFeature {...props} isEnrolled={isEnrolledInAccelerator} />;
}

GoogleAiPathPage.getLayout = getLayout;
